'use client'

import { Value } from '@firebase/remote-config'
import cx from 'classnames'
import { usePathname, useSearchParams } from 'next/navigation'
import React, { Suspense, useCallback, useEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import Frame from '@/components/Common/Frame'
import ScrollSpy from '@/components/Common/ScrollSpy'
import { impressionIdState } from '@/components/Context/ImpressionProvider'
import { jwtTokenState, loginUserState } from '@/components/Context/JwtTokenProvider'
import { deliveryDateDefaultValue } from '@/components/Form/Atoms/DeliveryDateInput'
import { deliveryHourDefaultValue } from '@/components/Form/Atoms/DeliveryHourInput'
import SurveyForm from '@/components/Form/Organisms/SurveyForm'
import UnderSurveyForm from '@/components/Form/Organisms/SurveyForm/UnderSurveyForm'
import { AnimationChildrenProps } from '@/components/LandingPage/ContentModel/Animation'
import VerticalAnimation from '@/components/LandingPage/ContentModel/Animation/VerticalAnimation'
import Box from '@/components/LandingPage/ContentModel/Box'
import Hr from '@/components/LandingPage/ContentModel/Hr'
import Image from '@/components/LandingPage/ContentModel/Image'
import Layout from '@/components/LandingPage/ContentModel/Layout'
import Letro from '@/components/LandingPage/ContentModel/Letro'
import ProductCards from '@/components/LandingPage/ContentModel/ProductCards'
import Review from '@/components/LandingPage/ContentModel/Review'
import IntroductionView from '@/components/LandingPage/ContentModel/Teaser/Introduction/IntroductionView'
import Text from '@/components/LandingPage/ContentModel/Text'
import Video from '@/components/LandingPage/ContentModel/Video'
import { Props as OptimizerProps } from '@/components/Optimizer/ClientOptimizer'
import SimpleOrderConfirm from '@/components/Order/SimpleOrderConfirm'
import { useAddCart } from '@/components/hooks/useAddCart'
import { useEvent } from '@/components/hooks/useRecordEvent'
import { useSmoothScroll } from '@/components/hooks/useSmoothScroll'
import { saveOrder } from '@/serverActions/saveOrder'
import { CampaignType } from '@/types/Campaign'
import { ContentModelIntroductionProductType } from '@/types/ContentModel/ContentModeIntroductionProduct'
import { ContentModelType } from '@/types/ContentModel/ContentModel'
import { ContentModelBoxType } from '@/types/ContentModel/ContentModelBox'
import { ContentModelFrameType } from '@/types/ContentModel/ContentModelFrame'
import { ContentModelHrType } from '@/types/ContentModel/ContentModelHr'
import { ContentModelImageType } from '@/types/ContentModel/ContentModelImage'
import { ContentModelLayoutType } from '@/types/ContentModel/ContentModelLayout'
import { ContentModelLetroType } from '@/types/ContentModel/ContentModelLetro'
import { ContentModelProductCardsType } from '@/types/ContentModel/ContentModelProductCards'
import { ContentModelReviewType } from '@/types/ContentModel/ContentModelReview'
import { ContentModelSurveyType } from '@/types/ContentModel/ContentModelSurvey'
import { ContentModelTextType } from '@/types/ContentModel/ContentModelText'
import { ContentModelVideoType } from '@/types/ContentModel/ContentModelVideo'
import { OrderType } from '@/types/order'
import { ProductType } from '@/types/product'
import { lineFriendRedirectPath } from '@/utils/const'
import { addBrowsingParameter, addParameter, addParameters } from '@/utils/urlUtils'

type GenerateElementProps = (_: AnimationChildrenProps) => React.ReactNode
export type Props = {
  baseline?: boolean
  contentModel: ContentModelType
  optimizer?: OptimizerProps
  firstView?: boolean
  product?: ProductType | null
  campaign?: CampaignType | null
  optimizerParameters?: Record<string, Value>
  afterSubmit?: (() => void) | null
}

export default function ContentModelLogic({
  contentModel,
  product = null,
  campaign = null,
  baseline = true,
  optimizer = {},
  firstView = false,
  optimizerParameters = {},
  afterSubmit = null,
}: Props) {
  const loginUser = useRecoilValue(loginUserState)
  const setRecoilLoginSession = useSetRecoilState(jwtTokenState)
  const setRecoilLoginUser = useSetRecoilState(loginUserState)

  const [optimizerContentModel, setRemoteConfigContentModel] = useState<ContentModelType | null>(
    contentModel.optimizer?.defaultParameter || null
  )
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const { record } = useEvent()
  const [startSimpleConfirm, setStartSimpleConfirm] = useState<boolean>(false)
  const [newOrder, setNewOrder] = useState<OrderType | null>(null)

  useEffect(() => {
    const parameterValue = optimizerParameters[contentModel.optimizer?.parameterKey!]
    if (parameterValue != null) {
      try {
        const fetchedContentModelByRemoteConfig = JSON.parse(parameterValue.asString())
        if (fetchedContentModelByRemoteConfig != null && optimizerContentModel != fetchedContentModelByRemoteConfig) {
          setRemoteConfigContentModel(
            contentModel.optimizer?.injection
              ? {
                  ...contentModel,
                  ...fetchedContentModelByRemoteConfig,
                }
              : fetchedContentModelByRemoteConfig
          )
        }
      } catch (error) {
        console.error(error)
      }
    }
  }, [optimizerParameters])

  const generateElement = useCallback(({ animationRef = null, className = '', style = {} }: AnimationChildrenProps) => {
    switch (contentModel.type) {
      case 'Layout':
        return (
          <Suspense>
            <Layout
              product={product}
              campaign={campaign}
              contentModel={contentModel as ContentModelLayoutType}
              optimizer={optimizer}
              firstView={firstView}
              afterSubmit={afterSubmit}
            />
          </Suspense>
        )
      case 'Box':
        return (
          <Suspense>
            <Box
              contentModel={contentModel as ContentModelBoxType}
              animationRef={animationRef}
              product={product}
              campaign={campaign}
              style={style}
              className={className}
              optimizer={optimizer}
              firstView={firstView}
              afterSubmit={afterSubmit}
            />
          </Suspense>
        )
      case 'Frame':
        return (
          <Suspense>
            <Frame value={(contentModel as ContentModelFrameType).value} />
          </Suspense>
        )
      case 'Letro':
        return (
          <Suspense>
            <Letro gadgetId={(contentModel as ContentModelLetroType).gadgetId} />
          </Suspense>
        )
      case 'Review':
        return (
          <Suspense>
            <Review
              productId={(contentModel as ContentModelReviewType).productId}
              detail={(contentModel as ContentModelReviewType).detail == true}
              carousel={(contentModel as ContentModelReviewType).carousel == true}
              perPage={(contentModel as ContentModelReviewType).perPage || 10}
              ageTag={(contentModel as ContentModelReviewType).ageTag == true}
            />
          </Suspense>
        )
      case 'Text':
        return (
          <Suspense>
            <Text
              contentModel={contentModel as ContentModelTextType}
              animationRef={animationRef}
              style={style}
              className={className}
            />
          </Suspense>
        )
      case 'Hr':
        return (
          <Suspense>
            <Hr contentModel={contentModel as ContentModelHrType} />
          </Suspense>
        )
      case 'Br':
        return <br />
      case 'Image':
        return (
          <Suspense>
            <Image
              contentModel={contentModel as ContentModelImageType}
              animationRef={animationRef}
              style={style}
              className={className}
              firstView={firstView}
            />
          </Suspense>
        )
      case 'Video':
        return (
          <Suspense>
            <Video
              contentModel={contentModel as ContentModelVideoType}
              animationRef={animationRef}
              style={style}
              className={className}
            />
          </Suspense>
        )
      case 'TeaserIntroductionProduct':
        return (
          <Suspense>
            <IntroductionView
              eyecatch={(contentModel as ContentModelIntroductionProductType).eyecatch || '2月3日 伊勢丹新宿店OPEN'}
            />
          </Suspense>
        )
      case 'ProductCards':
        return (
          <Suspense>
            <ProductCards contentModel={contentModel as ContentModelProductCardsType} />
          </Suspense>
        )
      case 'Survey':
        return (
          <Suspense>
            {(contentModel as ContentModelSurveyType).under == true ? (
              <UnderSurveyForm
                symbol={(contentModel as ContentModelSurveyType).symbol}
                productIds={[]}
                nps={(contentModel as ContentModelSurveyType).nps == true}
              />
            ) : (
              <SurveyForm
                symbol={(contentModel as ContentModelSurveyType).symbol}
                nps={(contentModel as ContentModelSurveyType).nps == true}
              />
            )}
          </Suspense>
        )
      default:
        return <></>
    }
  }, [])

  const wrapScrollSpy = useCallback((element: React.JSX.Element) => {
    if (contentModel.scrollSpy) {
      return (
        <ScrollSpy id={contentModel.scrollSpy} type={'menu'}>
          {element}
        </ScrollSpy>
      )
    } else {
      return <>{element}</>
    }
  }, [])

  const injectionAnimation = useCallback((generateElement: GenerateElementProps) => {
    if (contentModel.animation == 'vertical') {
      return <VerticalAnimation targetComponent={(props) => generateElement(props)} />
    } else {
      return <>{generateElement({})}</>
    }
  }, [])

  const style = {
    ...(contentModel.padding
      ? {
          padding: contentModel.padding,
        }
      : {}),
    ...(contentModel.margin
      ? {
          margin: contentModel.margin,
        }
      : {}),
    ...(contentModel.backgroundColor
      ? {
          backgroundColor: contentModel.backgroundColor,
        }
      : {}),
    ...(contentModel.backgroundImage && contentModel.backgroundImage.creative
      ? {
          backgroundImage: `url(${addParameter(contentModel.backgroundImage.creative.publishUrl, 'width', '390')})`,
          backgroundPosition: 'center',
          backgroundSize: contentModel.backgroundImage.aspectMode || 'cover',
        }
      : {}),
    ...(contentModel.zIndex
      ? {
          zIndex: contentModel.zIndex,
        }
      : {}),
    ...(contentModel.height
      ? {
          height: contentModel.height,
        }
      : {}),
    ...(contentModel.width
      ? {
          width: contentModel.width,
        }
      : {}),
    ...(contentModel.maxWidth
      ? {
          maxWidth: contentModel.maxWidth,
        }
      : {}),
    ...(contentModel.textAlign
      ? {
          textAlign: contentModel.textAlign,
        }
      : {}),
    ...(contentModel.position
      ? {
          position: contentModel.position,
        }
      : {}),
    ...(contentModel.top
      ? {
          top: contentModel.top,
        }
      : {}),
    ...(contentModel.left
      ? {
          left: contentModel.left,
        }
      : {}),
    ...(contentModel.right
      ? {
          right: contentModel.right,
        }
      : {}),
    ...(contentModel.bottom
      ? {
          bottom: contentModel.bottom,
        }
      : {}),
    ...(contentModel.borderRadius
      ? {
          borderRadius: contentModel.borderRadius,
        }
      : {}),
    ...(contentModel.lineHeight
      ? {
          lineHeight: contentModel.lineHeight,
        }
      : {}),
    ...(contentModel.opacity
      ? {
          opacity: contentModel.opacity,
        }
      : {}),
    ...(contentModel.overflow
      ? {
          overflow: contentModel.overflow,
        }
      : {}),
    ...(contentModel.action?.type != null
      ? {
          cursor: 'pointer',
        }
      : {}),
    ...(contentModel.border
      ? {
          border: contentModel.border,
        }
      : {}),
    ...(contentModel.borderRight
      ? {
          borderRight: contentModel.borderRight,
        }
      : {}),
    ...(contentModel.borderLeft
      ? {
          borderLeft: contentModel.borderLeft,
        }
      : {}),
    ...(contentModel.borderBottom
      ? {
          borderBottom: contentModel.borderBottom,
        }
      : {}),
    ...(contentModel.borderTop
      ? {
          borderTop: contentModel.borderTop,
        }
      : {}),
    ...(contentModel.display
      ? {
          display: contentModel.display,
        }
      : {}),
    ...(contentModel.flex
      ? {
          flex: contentModel.flex,
        }
      : {}),
    ...(contentModel.display
      ? {
          display: contentModel.display,
        }
      : {}),
  }

  const actionObject = contentModel.action
  const impressionId: string | null = useRecoilValue(impressionIdState)
  const productId =
    actionObject && actionObject.type === 'addCart' && 'productId' in actionObject
      ? actionObject.productId
      : product?.productId
  const productIds =
    actionObject && actionObject.type === 'addCart' && 'productIds' in actionObject ? actionObject.productIds : null
  const cartPageType =
    actionObject && actionObject.type === 'addCart' && 'cartPageType' in actionObject
      ? actionObject.cartPageType
      : 'market'
  const cartVersion =
    actionObject && actionObject.type === 'addCart' && 'cartVersion' in actionObject ? actionObject.cartVersion : 'new'
  const campaignId = campaign?.campaignId
  const { click } = useAddCart({
    ...(productId ? { productId } : {}),
    ...(productIds ? { productIds } : {}),
    ...(campaignId ? { campaignId } : {}),
    ...(cartPageType ? { cartPageType } : {}),
    ...(cartVersion ? { cartVersion } : {}),
    ...(afterSubmit != null
      ? { afterSubmit }
      : actionObject && 'afterReload' in actionObject && actionObject.afterReload == true
        ? {
            afterSubmit: () => {
              window.location.reload()
            },
          }
        : {}),
    ...(loginUser != null && actionObject && 'withConfirm' in actionObject && actionObject.withConfirm == true
      ? {
          afterSubmit: async () => {
            const shippingAddress = {
              lastName: loginUser.shippingAddress.lastName,
              firstName: loginUser.shippingAddress.firstName,
              phoneNumber: loginUser.shippingAddress.phoneNumber,
              postalCode: loginUser.shippingAddress.postalCode,
              prefecture: loginUser.shippingAddress.prefecture,
              city: loginUser.shippingAddress.city,
              addressLine: loginUser.shippingAddress.addressLine,
            }

            const { success, order, loginSession } = await saveOrder({
              lineIdToken: null,
              liffId: null,
              buyerToken: null,
              shippingAddress,
              destinationShippingAddress: shippingAddress,
              temporaryProfileFlag: 'continuation',
              email: null,
              password: '',
              sex: null,
              birthday: null,
              paymentMethod: loginUser.suggestedPaymentMethod,
              deliveryDate: deliveryDateDefaultValue({ prefecture: shippingAddress.prefecture, deliveryDate: null }),
              deliveryHour: deliveryHourDefaultValue,
              impressionId,
            })

            if (success) {
              setRecoilLoginSession(true)
              setRecoilLoginUser(loginSession.user)

              setNewOrder(order)
            } else {
              window.location.href = addBrowsingParameter(`/${cartPageType || 'market'}-carts`)
            }
          },
        }
      : {}),
    extra: {
      [pathname]: 'pathname',
      ...(cartPageType ? { cartPageType } : {}),
      ...(cartVersion ? { cartVersion } : {}),
      ...(actionObject && 'measurementId' in actionObject && actionObject.measurementId != undefined
        ? { measurementId: actionObject.measurementId }
        : {}),
    },
  })
  const { scrollTo } = useSmoothScroll()

  const onClick = useCallback(
    actionObject && actionObject.type == 'addCart' && impressionId != null
      ? // @ts-ignore
        async () => {
          if (actionObject.withConfirm) {
            setStartSimpleConfirm(true)
          }
          click(impressionId)
        }
      : actionObject && actionObject.type == 'link' && 'href' in actionObject
        ? () => {
            record('click_link', {
              href: actionObject.href,
              ...('measurementId' in actionObject && actionObject.measurementId != undefined
                ? { measurementId: actionObject.measurementId }
                : {}),
            })

            if (actionObject.target == '_blank') {
              window.open(actionObject.href, '_blank')
            } else {
              window.location.href = addBrowsingParameter(actionObject.href)
            }
          }
        : actionObject && actionObject.type == 'scroll' && 'id' in actionObject
          ? () => {
              record('click_link', {
                type: 'scroll',
                ...('measurementId' in actionObject && actionObject.measurementId != undefined
                  ? { measurementId: actionObject.measurementId }
                  : {}),
              })
              scrollTo(actionObject.id)
            }
          : actionObject && actionObject.type == 'lineConnect'
            ? () => {
                record('add_line_friend', {
                  ...('measurementId' in actionObject && actionObject.measurementId != undefined
                    ? { measurementId: actionObject.measurementId }
                    : {}),
                })
                window.location.href = addParameters('/line/liff/connect', {
                  ...('parameters' in actionObject && actionObject.parameters != undefined
                    ? actionObject.parameters
                    : {}),
                })
              }
            : actionObject && actionObject.type == 'lineFriend'
              ? () => {
                  let url = lineFriendRedirectPath
                  let parameters: Record<string, string> = {
                    // keyが上書かれるので一意になるようにしたい
                    [pathname]: 'pathname',
                    ...(searchParams.toString() != ''
                      ? Object.fromEntries(
                          searchParams
                            .toString()
                            .split('&')
                            .map((item) => item.split('='))
                        )
                      : {}),
                    ...('measurementId' in actionObject && actionObject.measurementId != undefined
                      ? { measurementId: actionObject.measurementId }
                      : {}),
                    ...('parameters' in actionObject && actionObject.parameters != undefined
                      ? actionObject.parameters
                      : {}),
                  }
                  if ('liffId' in actionObject && 'liffActionType' in actionObject) {
                    parameters = { ...parameters, liffActionType: actionObject.liffActionType }
                    if (actionObject.liffActionType == 'closeWindow') {
                      url = `line://app/${actionObject.liffId}`
                      if ('notificationTemplateId' in actionObject && actionObject.notificationTemplateId != null) {
                        parameters = { ...parameters, notificationTemplateId: actionObject.notificationTemplateId }
                      }
                    } else if (
                      actionObject.liffActionType == 'freePurchase' ||
                      actionObject.liffActionType == 'purchase'
                    ) {
                      url = `line://app/${actionObject.liffId}`
                      parameters = {
                        ...parameters,
                        productId:
                          'productId' in actionObject && actionObject.productId != undefined
                            ? actionObject.productId
                            : product?.productId!,
                        ...('contentId' in actionObject && actionObject.contentId != undefined
                          ? { contentId: actionObject.contentId }
                          : {}),
                      }
                    }
                  }

                  record('add_line_friend', {
                    ...('measurementId' in actionObject && actionObject.measurementId != undefined
                      ? { measurementId: actionObject.measurementId }
                      : {}),
                  })
                  window.location.href = addParameters(url, parameters)
                }
              : () => {},
    [impressionId, loginUser]
  )

  return (
    <>
      {startSimpleConfirm && loginUser != null ? (
        <>
          <SimpleOrderConfirm
            loginUser={loginUser}
            order={newOrder}
            onClick={() => {
              setStartSimpleConfirm(false)
            }}
          />
        </>
      ) : (
        <></>
      )}
      {optimizerContentModel == null ? (
        <>
          {wrapScrollSpy(
            Object.keys(style).length > 0 ? (
              baseline ? (
                <span
                  style={style}
                  onClick={onClick}
                  className={cx({
                    'u-pc': contentModel.sp == false,
                    'u-sp': contentModel.pc == false,
                  })}
                >
                  {injectionAnimation(generateElement)}
                </span>
              ) : (
                <div
                  style={style}
                  onClick={onClick}
                  className={cx({
                    'u-pc': contentModel.sp == false,
                    'u-sp': contentModel.pc == false,
                  })}
                >
                  {injectionAnimation(generateElement)}
                </div>
              )
            ) : (
              generateElement({
                className: cx({
                  'u-pc': contentModel.sp == false,
                  'u-sp': contentModel.pc == false,
                }),
              })
            )
          )}
        </>
      ) : (
        <ContentModelLogic
          contentModel={optimizerContentModel}
          product={product}
          campaign={campaign}
          baseline={baseline}
          optimizer={optimizer}
          firstView={firstView}
        />
      )}
    </>
  )
}
